import { DEBOUNCE_MS } from '@/constants/timing';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { Role } from '@/models/AccountEnums';
import {
  BankCategoryOverride,
  useDeleteBankCategoryOverrideMutation,
  useGetBankCategoryOverridesQuery,
  useGetCashflowCategoriesQuery,
  useUpdateBankCategoryOverrideMutation,
} from '@/store/cashFlowApi';
import { WARootState } from '@/store/store';
import {
  TransactionTypeOverride,
  useDeleteTransactionTypeOverrideMutation,
  useGetTransactionTypeOverridesQuery,
  useUpdateTransactionTypeOverrideMutation,
} from '@/store/transactionsApi';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Theme,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModel,
  GridRowParams,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { useSelector } from 'react-redux';
import CustomToolbarForNative, {
  CustomToolbarForNativeProps,
} from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import { actionColumnConfig } from '@/components/grids/gridColumnConfigs';
import { GridContainer } from '../../components/grids/GridContainer';

interface LocalCategory {
  BankTransactionCategoryId: number;
  Name: string;
  ParentId: number | null;
}

/** Extracts a human-readable message from an RTK Query error (or any thrown value). */
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
    return JSON.stringify(error);
  }
  return 'Unknown error';
}

const comparisonOperators = [
  { value: 1, label: 'Is' },
  { value: 2, label: 'Contains' },
  { value: 3, label: 'Begins With' },
  { value: 4, label: 'Ends With' },
];

const comparisonOperatorMap = comparisonOperators.reduce<{
  [key: number]: string;
}>((acc, op) => {
  acc[op.value] = op.label;
  return acc;
}, {});

interface ManageRulesModalProps {
  open: boolean;
  onClose: () => void;
  investorId?: number;
}

const modalStyle = (theme: Theme) => ({
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 900,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  [theme.breakpoints.down(1001)]: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    maxHeight: '100%',
    top: 0,
    left: 0,
    transform: 'none',
    borderRadius: 0,
    p: 2,
    paddingTop: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
  },
  paddingBottom:
    'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
});

const tableContainerStyle = {
  // maxHeight: 'calc(90vh - 250px)',
  // overflowY: 'auto',
  marginTop: 2,
  flexGrow: 1,
};

// --- Custom Edit Cell Component for Category --- START
interface CategoryEditInputCellProps extends GridRenderEditCellParams {
  categoryOptions: { value: string; label: string }[];
  isLoading: boolean;
}

function CategoryEditInputCell(props: CategoryEditInputCellProps) {
  const { id, field, categoryOptions, isLoading } = props;
  const apiRef = useGridApiContext();
  const initialValue = typeof props.value === 'string' ? props.value : '';
  const [inputValue, setInputValue] = useState(initialValue);

  const handleValueChange = (
    _event: React.SyntheticEvent,
    newValue: string | null,
  ) => {
    setInputValue(newValue || '');
    // Set the value in the grid's internal state
    void apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <Autocomplete
      fullWidth
      value={inputValue} // The current value of the text field/selection
      onChange={handleValueChange}
      options={categoryOptions.map((option) => option.value)} // Provide only the string names as options
      loading={isLoading}
      getOptionLabel={(option) => option} // How to display options
      isOptionEqualToValue={(option, val) => option === val}
      renderInput={(params) => (
        <TextField
          {...params}
          variant='standard' // Match grid style
          slotProps={{
            input: { ...params.InputProps, disableUnderline: true },
          }}
          size='small'
          autoFocus
        />
      )}
      sx={{ pl: 1, height: '100%', display: 'flex', alignItems: 'center' }} // Styling to fit the cell
    />
  );
}
// --- Custom Edit Cell Component for Category --- END

const ManageRulesModal: React.FC<ManageRulesModalProps> = ({
  open,
  onClose,
  investorId,
}) => {
  const apiRef = useGridApiRef();
  const { t } = useLocalization();
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';

  // State for Cash Flow Grid
  const [loadingRowIds, setLoadingRowIds] = useState<Set<number>>(new Set());
  const [deletingCashFlowRuleId, setDeletingCashFlowRuleId] = useState<
    number | null
  >(null);
  const [optimisticallyHiddenCashFlowIds, setOptimisticallyHiddenCashFlowIds] =
    useState<Set<number>>(new Set());

  // State for Transaction Type Grid
  const [loadingTransactionTypeRowIds, setLoadingTransactionTypeRowIds] =
    useState<Set<number>>(new Set());
  const [deletingTransactionTypeRuleId, setDeletingTransactionTypeRuleId] =
    useState<number | null>(null);
  const [
    optimisticallyHiddenTransactionTypeIds,
    setOptimisticallyHiddenTransactionTypeIds,
  ] = useState<Set<number>>(new Set());

  // Delete confirmation dialog state for Transaction Type rules
  const [txTypeDeleteDialogOpen, setTxTypeDeleteDialogOpen] = useState(false);
  const [txTypeRulePendingDelete, setTxTypeRulePendingDelete] =
    useState<TransactionTypeOverride | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const authIsLoaded = useAuthIsLoaded();

  const {
    data: cashFlowOverridesData,
    isLoading: isLoadingCashFlowRules,
    isError: isErrorCashFlowRules,
    error: errorCashFlowRules,
  } = useGetBankCategoryOverridesQuery(
    { InvestorId: investorId },
    {
      skip: !authIsLoaded || currentRole !== Role.Investor,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useGetCashflowCategoriesQuery(
      { InvestorId: investorId },
      { skip: !authIsLoaded },
    );

  const { categoryNameToIdMap, categoryIdToNameMap, categoryIdToParentIdMap } =
    useMemo(() => {
      const parents: LocalCategory[] = [];
      const subOptionsMap: { [parentId: number]: LocalCategory[] } = {};
      const nameToIdMap: { [name: string]: number } = {};
      const idToNameMap: { [id: number]: string } = {};
      const idToParentIdMap: { [id: number]: number | null } = {};
      const parentMap: { [id: number]: LocalCategory } = {};

      if (categoriesData?.categories) {
        categoriesData.categories.forEach((cat) => {
          const localCat: LocalCategory = {
            ...cat,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ParentId: cat.ParentId ?? null,
          };
          idToNameMap[cat.BankTransactionCategoryId] = cat.Name;
          nameToIdMap[cat.Name] = cat.BankTransactionCategoryId;
          idToParentIdMap[cat.BankTransactionCategoryId] = localCat.ParentId;

          if (!cat.ParentId) {
            parents.push(localCat);
            parentMap[cat.BankTransactionCategoryId] = localCat;
            subOptionsMap[cat.BankTransactionCategoryId] = [];
          }
        });

        categoriesData.categories.forEach((cat) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (cat.ParentId && parentMap[cat.ParentId]) {
            const subCat: LocalCategory = { ...cat, ParentId: cat.ParentId };
            subOptionsMap[cat.ParentId].push(subCat);
          }
        });
      }
      parents.sort((a, b) => a.Name.localeCompare(b.Name));
      Object.values(subOptionsMap).forEach((arr) =>
        arr.sort((a, b) => a.Name.localeCompare(b.Name)),
      );

      return {
        parentCategoryOptions: parents,
        subCategoryOptionsMap: subOptionsMap,
        categoryNameToIdMap: nameToIdMap,
        categoryIdToNameMap: idToNameMap,
        categoryIdToParentIdMap: idToParentIdMap,
      };
    }, [categoriesData]);

  const {
    data: transactionTypeOverridesData,
    isLoading: isLoadingTransactionTypeRules,
    isError: isErrorTransactionTypeRules,
    error: errorTransactionTypeRules,
  } = useGetTransactionTypeOverridesQuery(
    { InvestorId: investorId },
    {
      skip: !authIsLoaded || currentRole !== Role.Investor,
      refetchOnMountOrArgChange: true,
    },
  );

  const [deleteBankCategoryOverride] = useDeleteBankCategoryOverrideMutation();
  const [updateBankCategoryOverride] = useUpdateBankCategoryOverrideMutation();
  const [updateTransactionTypeOverride] =
    useUpdateTransactionTypeOverrideMutation();
  const [deleteTransactionTypeOverride] =
    useDeleteTransactionTypeOverrideMutation();

  const handleDeleteCashFlowRule = useCallback(
    async (ruleId: number) => {
      setDeletingCashFlowRuleId(ruleId);
      // Optimistically hide
      setOptimisticallyHiddenCashFlowIds((prev) => new Set(prev).add(ruleId));
      try {
        await deleteBankCategoryOverride({
          overrideId: ruleId,
          InvestorId: investorId,
        }).unwrap();
        setSnackbar({
          open: true,
          message: 'Cash flow rule deleted.',
          severity: 'success',
        });
        // Keep hidden on success (actual data refetch will confirm)
      } catch (error) {
        console.error('Error deleting cash flow rule:', ruleId, error);
        setSnackbar({
          open: true,
          message: 'Failed to delete cash flow rule.',
          severity: 'error',
        });
        // Revert optimistic hide on error
        setOptimisticallyHiddenCashFlowIds((prev) => {
          const next = new Set(prev);
          next.delete(ruleId);
          return next;
        });
      } finally {
        setDeletingCashFlowRuleId(null);
      }
    },
    [deleteBankCategoryOverride, investorId],
  );

  const processCashFlowRowUpdate = useCallback(
    async (
      newRow: BankCategoryOverride,
      oldRow: BankCategoryOverride,
    ): Promise<BankCategoryOverride> => {
      const descriptionChanged = newRow.Description !== oldRow.Description;
      const operatorChanged =
        newRow.ComparisonOperator !== oldRow.ComparisonOperator;
      const categoryChanged = newRow.CategoryName !== oldRow.CategoryName;
      if (!descriptionChanged && !operatorChanged && !categoryChanged) {
        return oldRow;
      }

      setLoadingRowIds((prev) =>
        new Set(prev).add(newRow.WealthAccountBankTransactionOverrideId),
      );
      const rowId = newRow.WealthAccountBankTransactionOverrideId;

      try {
        let targetCategoryId = oldRow.BankTransactionCategoryId;
        if (categoryChanged) {
          if (newRow.CategoryName in categoryNameToIdMap) {
            targetCategoryId = categoryNameToIdMap[newRow.CategoryName];
          } else {
            throw new Error(`Invalid category name: ${newRow.CategoryName}`);
          }
        }

        const payload: BankCategoryOverride = {
          ...oldRow,
          ...newRow,
          WealthAccountBankTransactionOverrideId: rowId,
          BankTransactionCategoryId: targetCategoryId,
          CategoryName:
            categoryIdToNameMap[targetCategoryId] || newRow.CategoryName,
          ComparisonOperatorDisplay:
            comparisonOperatorMap[newRow.ComparisonOperator] ||
            oldRow.ComparisonOperatorDisplay,
        };

        const updatedRule = await updateBankCategoryOverride({
          overrideId: rowId,
          overrideData: payload,
          InvestorId: investorId,
        }).unwrap();

        setSnackbar({
          open: true,
          message: 'Rule updated successfully!',
          severity: 'success',
        });

        const finalRowState = { ...newRow, ...updatedRule };
        return finalRowState;
      } catch (error: unknown) {
        console.error(`Failed to update rule ${String(rowId)}:`, error);
        setSnackbar({
          open: true,
          message: `Failed to update rule: ${getErrorMessage(error)}`,
          severity: 'error',
        });
        return oldRow; // Revert grid on error
      } finally {
        setLoadingRowIds((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
      }
    },
    [
      updateBankCategoryOverride,
      categoryIdToNameMap,
      categoryNameToIdMap,
      setSnackbar,
      investorId,
    ],
  );

  const onProcessRowUpdateError = useCallback((error: unknown) => {
    console.error('Error processing row update callback:', error);
  }, []);

  const handleDeleteTransactionTypeRule = async (ruleId: number) => {
    setDeletingTransactionTypeRuleId(ruleId);
    // Optimistically hide
    setOptimisticallyHiddenTransactionTypeIds((prev) =>
      new Set(prev).add(ruleId),
    );
    try {
      const success = await deleteTransactionTypeOverride({
        overrideId: ruleId,
        InvestorId: investorId,
      }).unwrap();
      if (success) {
        setSnackbar({
          open: true,
          message: 'Transaction type rule deleted.',
          severity: 'success',
        });
        // Keep hidden on success
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to delete transaction type rule (API).',
          severity: 'error',
        });
        // Revert optimistic hide on API failure
        setOptimisticallyHiddenTransactionTypeIds((prev) => {
          const next = new Set(prev);
          next.delete(ruleId);
          return next;
        });
      }
    } catch (error) {
      console.error('Error deleting transaction type rule:', ruleId, error);
      setSnackbar({
        open: true,
        message: 'Failed to delete transaction type rule.',
        severity: 'error',
      });
      // Revert optimistic hide on exception
      setOptimisticallyHiddenTransactionTypeIds((prev) => {
        const next = new Set(prev);
        next.delete(ruleId);
        return next;
      });
    } finally {
      setDeletingTransactionTypeRuleId(null);
    }
  };

  const confirmDeleteTransactionTypeRule = (rule: TransactionTypeOverride) => {
    setTxTypeRulePendingDelete(rule);
    setTxTypeDeleteDialogOpen(true);
  };

  const handleConfirmTxTypeDelete = async () => {
    if (!txTypeRulePendingDelete) return;
    const ruleId =
      txTypeRulePendingDelete.WealthAccountTransactionTypeOverrideId;
    setTxTypeDeleteDialogOpen(false);
    setTxTypeRulePendingDelete(null);
    await handleDeleteTransactionTypeRule(ruleId);
  };

  const handleCancelTxTypeDelete = () => {
    setTxTypeDeleteDialogOpen(false);
    setTxTypeRulePendingDelete(null);
  };

  const processTransactionTypeRowUpdate = useCallback(
    async (
      newRow: TransactionTypeOverride,
      oldRow: TransactionTypeOverride,
    ): Promise<TransactionTypeOverride> => {
      const rowId = newRow.WealthAccountTransactionTypeOverrideId;

      // --- Change Detection --- START
      const descriptionChanged =
        newRow.DisplayDescription !== oldRow.DisplayDescription;
      const operatorChanged =
        newRow.ComparisonOperator !== oldRow.ComparisonOperator;

      if (!descriptionChanged && !operatorChanged) {
        // console.log(`No changes detected for Transaction Type Rule ${rowId}. Skipping save.`);
        return oldRow; // Return oldRow to exit edit mode without saving
      }
      // --- Change Detection --- END

      // console.log(`Changes detected for Transaction Type Rule ${rowId}. Proceeding with save.`);
      setLoadingTransactionTypeRowIds((prev) => new Set(prev).add(rowId));

      try {
        // Prepare the full payload
        const payload: TransactionTypeOverride = {
          ...oldRow,
          DisplayDescription: newRow.DisplayDescription,
          ComparisonOperator: newRow.ComparisonOperator,
          ComparisonOperatorDisplay:
            comparisonOperatorMap[newRow.ComparisonOperator] ||
            oldRow.ComparisonOperatorDisplay,
          WealthAccountTransactionTypeOverrideId: rowId,
          TransactionTypeId: oldRow.TransactionTypeId,
          TransactionTypeDisplay: oldRow.TransactionTypeDisplay,
        };

        // console.log("Calling updateTransactionTypeOverride with payload:", payload);
        const updatedRule = await updateTransactionTypeOverride({
          overrideId: rowId,
          overrideData: payload,
          InvestorId: investorId,
        }).unwrap();

        setSnackbar({
          open: true,
          message: 'Transaction type rule updated!',
          severity: 'success',
        });

        // Return merged object for grid state
        const finalRowState = { ...newRow, ...updatedRule };
        return finalRowState;
      } catch (error: unknown) {
        console.error(
          `Failed to update transaction type rule ${String(rowId)}:`,
          error,
        );
        setSnackbar({
          open: true,
          message: `Failed to update rule: ${getErrorMessage(error)}`,
          severity: 'error',
        });
        return oldRow; // Revert grid on error
      } finally {
        setLoadingTransactionTypeRowIds((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
      }
    },
    [updateTransactionTypeOverride, investorId],
  );

  const handleSnackbarClose = () => {
    setSnackbar(null);
  };

  // Memoize category options specifically for the Autocomplete
  const categoryOptionsForAutocomplete = useMemo(() => {
    if (!categoriesData) return [];
    return categoriesData.categories
      .map((cat) => ({
        // Autocomplete uses the `value` for the data, `label` for display in dropdown
        value: cat.Name,
        label: `${
          categoryIdToParentIdMap[cat.BankTransactionCategoryId] ? '  ' : ''
        }${cat.Name}`, // Indent subcategories for display
      }))
      .sort((a, b) => {
        // Sort logic (same as before)
        const idA = categoryNameToIdMap[a.value];
        const idB = categoryNameToIdMap[b.value];
        const parentIdA = categoryIdToParentIdMap[idA];
        const parentIdB = categoryIdToParentIdMap[idB];
        const isParentA = parentIdA === null;
        const isParentB = parentIdB === null;
        if (isParentA && !isParentB) return -1;
        if (!isParentA && isParentB) return 1;
        return a.label.trim().localeCompare(b.label.trim());
      });
  }, [categoriesData, categoryIdToParentIdMap, categoryNameToIdMap]);

  const cashFlowColumns: GridColDef<BankCategoryOverride>[] = useMemo(
    () => [
      {
        field: 'OriginalBankTransactionCategory',
        headerName: t('grid.originalCategory', 'Original Category'),
        flex: 1,
        editable: false,
      },
      {
        field: 'OriginalDisplayDescription',
        headerName: t('grid.originalDescription', 'Original Description'),
        flex: 1.5,
        editable: false,
      },
      {
        field: 'ComparisonOperator',
        headerName: t('grid.comparison', 'Comparison'),
        width: 130,
        editable: true,
        type: 'singleSelect',
        valueOptions: comparisonOperators.map((op) => ({
          value: op.value,
          label: op.label,
        })),
        valueGetter: (
          value: number,
          _row: GridRowModel<BankCategoryOverride>,
        ) => value,
        // Check if params.value is a valid number and exists as a key in the map
        // Fix linter error by checking value
        renderCell: (
          params: GridRenderCellParams<BankCategoryOverride, number>,
        ) => {
          if (
            typeof params.value === 'number' &&
            params.value in comparisonOperatorMap
          ) {
            return comparisonOperatorMap[params.value];
          }
          return params.value; // Return raw value if lookup fails
        },
      },
      {
        field: 'Description',
        headerName: t('description'),
        flex: 1.5,
        editable: true,
        renderCell: (
          params: GridRenderCellParams<BankCategoryOverride, string>,
        ) => params.value || <em style={{ color: 'grey' }}>(No Change)</em>,
      },
      {
        field: 'CategoryName',
        headerName: t('grid.category'),
        flex: 1,
        minWidth: GRID_COLUMN_WIDTH_NARROW,
        editable: !isLoadingCategories,
        // Keep type as singleSelect for filtering maybe? Or remove if renderEditCell handles all?
        // Let's remove type for now, rely on renderEditCell
        // type: "singleSelect",
        // valueOptions: ... (can be removed if not needed for filtering/display)

        // Render the custom Autocomplete component for editing
        renderEditCell: (params: GridRenderEditCellParams) => (
          <CategoryEditInputCell
            {...params}
            categoryOptions={categoryOptionsForAutocomplete}
            isLoading={isLoadingCategories}
          />
        ),
        // Render cell for display (still useful to show indentation)
        renderCell: (
          params: GridRenderCellParams<BankCategoryOverride, string>,
        ) => {
          if (typeof params.value !== 'string') return params.value;
          const categoryId = categoryNameToIdMap[params.value];
          if (typeof categoryId !== 'number') return params.value;
          const parentId = categoryIdToParentIdMap[categoryId] ?? null;
          const isSubcategory = parentId !== null;
          return isSubcategory ? `  ${params.value}` : params.value;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        width: 80,
        cellClassName: 'actions',
        ...actionColumnConfig,
        getActions: ({ row }: { row: BankCategoryOverride }) => {
          const id = row.WealthAccountBankTransactionOverrideId;
          const isDeleting = deletingCashFlowRuleId === id;
          const isLoading = loadingRowIds.has(id) || isDeleting;

          return [
            <GridActionsCellItem
              icon={
                isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />
              }
              label='Delete'
              onClick={() => {
                if (!isLoading) {
                  void handleDeleteCashFlowRule(id);
                }
              }}
              disabled={isLoading}
              color='inherit'
            />,
          ];
        },
      },
    ],
    [
      t,
      isLoadingCategories,
      categoryOptionsForAutocomplete,
      categoryNameToIdMap,
      categoryIdToParentIdMap,
      deletingCashFlowRuleId,
      loadingRowIds,
      handleDeleteCashFlowRule,
    ],
  );

  const transactionTypeColumns: GridColDef<TransactionTypeOverride>[] = useMemo(
    () => [
      {
        field: 'DisplayDescription',
        headerName: t('descriptionMatch', 'Description Match'),
        flex: 2,
        editable: true,
      },
      {
        field: 'ComparisonOperator',
        headerName: t('grid.comparison', 'Comparison'),
        width: 130,
        editable: true,
        type: 'singleSelect',
        valueOptions: comparisonOperators.map((op) => ({
          value: op.value,
          label: op.label,
        })),
        valueGetter: (
          value: number,
          _row: GridRowModel<TransactionTypeOverride>,
        ) => value,
        renderCell: (
          params: GridRenderCellParams<TransactionTypeOverride, number>,
        ) => {
          // Use the same logic as in cash flow columns
          if (
            typeof params.value === 'number' &&
            params.value in comparisonOperatorMap
          ) {
            return comparisonOperatorMap[params.value];
          }
          return params.value ?? 'N/A';
        },
      },
      {
        field: 'TransactionTypeDisplay',
        headerName: t('grid.resultingType', 'Resulting Type'),
        flex: 1,
        editable: false,
      },
      {
        field: 'actions',
        type: 'actions',
        width: 80,
        ...actionColumnConfig,
        getActions: ({ row }: { row: TransactionTypeOverride }) => {
          const id = row.WealthAccountTransactionTypeOverrideId;
          const isDeleting = deletingTransactionTypeRuleId === id; // Use specific state
          const isLoading = loadingTransactionTypeRowIds.has(id) || isDeleting;

          return [
            <IconButton
              size='small'
              onClick={() => {
                if (!isLoading) {
                  confirmDeleteTransactionTypeRule(row);
                }
              }}
              aria-label={t('delete')}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>,
          ];
        },
      },
    ],
    [t, deletingTransactionTypeRuleId, loadingTransactionTypeRowIds],
  );

  const cashFlowRows = useMemo(() => {
    return (
      cashFlowOverridesData?.Records.filter(
        (rule) =>
          !optimisticallyHiddenCashFlowIds.has(
            rule.WealthAccountBankTransactionOverrideId,
          ),
      ).map((rule) => ({
        id: rule.WealthAccountBankTransactionOverrideId,
        ...rule,
      })) || []
    );
  }, [cashFlowOverridesData, optimisticallyHiddenCashFlowIds]);

  const transactionTypeRows = useMemo(() => {
    return (
      transactionTypeOverridesData?.Records.filter(
        (rule) =>
          !optimisticallyHiddenTransactionTypeIds.has(
            rule.WealthAccountTransactionTypeOverrideId,
          ),
      ).map((rule) => ({
        id: rule.WealthAccountTransactionTypeOverrideId,
        ...rule,
      })) || []
    );
  }, [transactionTypeOverridesData, optimisticallyHiddenTransactionTypeIds]);

  // --- Combined Loading State --- Should consider loading state for each grid separately
  const isCashFlowLoading = isLoadingCashFlowRules || isLoadingCategories;
  const isTransactionTypeLoading = isLoadingTransactionTypeRules;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='manage-rules-modal-title'
      >
        <Box sx={modalStyle}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Typography
              id='manage-rules-modal-title'
              variant='h6'
              component='h2'
            >
              Manage Rules
            </Typography>
            <IconButton
              onClick={onClose}
              size='small'
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label='Rules tabs'
            >
              <Tab
                label='Cash Flow Rules'
                id='cash-flow-rules-tab'
                aria-controls='cash-flow-rules-panel'
              />
              <Tab
                label='Transaction Type Rules'
                id='transaction-type-rules-tab'
                aria-controls='transaction-type-rules-panel'
              />
            </Tabs>
          </Box>

          <TabPanel
            value={tabValue}
            index={0}
          >
            {isErrorCashFlowRules && (
              <Alert
                severity='error'
                sx={{ mt: 2 }}
              >
                Error loading cash flow rules:{' '}
                {getErrorMessage(errorCashFlowRules)}
              </Alert>
            )}
            <Box sx={{ ...tableContainerStyle }}>
              <GridContainer
                isWidget={false}
                size='flyout'
              >
                {cashFlowRows.length === 0 && !isCashFlowLoading ? (
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography
                      variant='body1'
                      fontWeight='bold'
                    >
                      {t('cashFlow.manageRules.noRulesYet')}
                    </Typography>
                    <Typography variant='body1'>
                      {t('cashFlow.manageRules.cashFlowRulesEmptyState')}
                    </Typography>
                  </Box>
                ) : (
                  <DataGridPremium
                    rows={cashFlowRows}
                    columns={cashFlowColumns}
                    apiRef={apiRef}
                    loading={isCashFlowLoading}
                    editMode='row'
                    processRowUpdate={processCashFlowRowUpdate}
                    onProcessRowUpdateError={onProcessRowUpdateError}
                    showToolbar
                    disablePivoting
                    slotProps={{
                      toolbar: {
                        ...defaultToolbarExportSlotProps,
                        showQuickFilter: true,
                        quickFilterProps: {
                          debounceMs: DEBOUNCE_MS.QUICK_FILTER_HEAVY,
                        },
                      },
                    }}
                    initialState={{
                      sorting: {
                        sortModel: [
                          { field: 'OriginalDisplayDescription', sort: 'asc' },
                        ],
                      },
                    }}
                    getRowClassName={(
                      params: GridRowParams<BankCategoryOverride>,
                    ) => {
                      if (loadingRowIds.has(params.id as number))
                        return 'row--loading';
                      if (deletingCashFlowRuleId === params.id)
                        return 'row--deleting';
                      return '';
                    }}
                    isCellEditable={(
                      params: GridCellParams<BankCategoryOverride>,
                    ) => {
                      const id = params.id as number;
                      return (
                        !loadingRowIds.has(id) && deletingCashFlowRuleId !== id
                      );
                    }}
                    getRowHeight={() => 'auto'}
                    sx={{
                      '& .row--loading': {
                        opacity: 0.6,
                        pointerEvents: 'none',
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                      '& .row--deleting': {
                        backgroundColor: (theme) =>
                          theme.palette.error.light + '80',
                        opacity: 0.7,
                        pointerEvents: 'none',
                      },
                      '& .MuiDataGrid-cell': {
                        whiteSpace: 'normal',
                        lineHeight: 1.4,
                        alignItems: 'start',
                        wordBreak: 'break-word',
                      },
                    }}
                  />
                )}
              </GridContainer>
            </Box>
          </TabPanel>

          <TabPanel
            value={tabValue}
            index={1}
          >
            {isErrorTransactionTypeRules && (
              <Alert
                severity='error'
                sx={{ mt: 2 }}
              >
                Error loading transaction type rules:{' '}
                {getErrorMessage(errorTransactionTypeRules)}
              </Alert>
            )}
            <Box sx={{ ...tableContainerStyle }}>
              <GridContainer
                isWidget={false}
                size='md'
              >
                {transactionTypeRows.length === 0 &&
                !isTransactionTypeLoading ? (
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography
                      variant='body1'
                      fontWeight='bold'
                    >
                      {t('cashFlow.manageRules.noRulesYet')}
                    </Typography>
                    <Typography variant='body1'>
                      {t('cashFlow.manageRules.transactionTypeRulesEmptyState')}
                    </Typography>
                  </Box>
                ) : (
                  <DataGridPremium
                    rows={transactionTypeRows}
                    columns={transactionTypeColumns}
                    loading={isTransactionTypeLoading}
                    editMode='row'
                    processRowUpdate={processTransactionTypeRowUpdate}
                    onProcessRowUpdateError={onProcessRowUpdateError}
                    showToolbar
                    disablePivoting
                    slotProps={{
                      toolbar: {
                        ...defaultToolbarExportSlotProps,
                        showQuickFilter: true,
                        quickFilterProps: {
                          debounceMs: DEBOUNCE_MS.QUICK_FILTER_HEAVY,
                        },
                      },
                    }}
                    slots={{
                      ...(isNative
                        ? {
                            toolbar: (props: CustomToolbarForNativeProps) => (
                              <CustomToolbarForNative {...props} />
                            ),
                          }
                        : {}),
                    }}
                    initialState={{
                      sorting: {
                        sortModel: [
                          { field: 'DisplayDescription', sort: 'asc' },
                        ],
                      },
                    }}
                    getRowClassName={(
                      params: GridRowParams<TransactionTypeOverride>,
                    ) => {
                      if (loadingTransactionTypeRowIds.has(params.id as number))
                        return 'row--loading';
                      if (deletingTransactionTypeRuleId === params.id)
                        return 'row--deleting';
                      return '';
                    }}
                    isCellEditable={(
                      params: GridCellParams<TransactionTypeOverride>,
                    ) => {
                      const id = params.id as number;
                      return (
                        !loadingTransactionTypeRowIds.has(id) &&
                        deletingTransactionTypeRuleId !== id
                      );
                    }}
                    getRowHeight={() => 'auto'}
                    sx={{
                      '& .row--loading': {
                        opacity: 0.6,
                        pointerEvents: 'none',
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                      '& .row--deleting': {
                        backgroundColor: (theme) =>
                          theme.palette.error.light + '80',
                        opacity: 0.7,
                        pointerEvents: 'none',
                      },
                      '& .MuiDataGrid-cell': {
                        whiteSpace: 'normal',
                        lineHeight: 1.4,
                        alignItems: 'start',
                        wordBreak: 'break-word',
                      },
                    }}
                  />
                )}
              </GridContainer>
            </Box>
          </TabPanel>
        </Box>
      </Modal>
      {/* Delete confirmation dialog for Transaction Type rule */}
      <Dialog
        open={txTypeDeleteDialogOpen}
        onClose={handleCancelTxTypeDelete}
        aria-labelledby='tx-type-delete-title'
        aria-describedby='tx-type-delete-description'
      >
        <DialogTitle id='tx-type-delete-title'>{'Are you sure?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id='tx-type-delete-description'>
            Are you sure you want to delete this transaction type rule?
            <br />
            <strong>{txTypeRulePendingDelete?.DisplayDescription || ''}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            onClick={handleCancelTxTypeDelete}
          >
            Cancel
          </Button>
          <Button
            size='large'
            variant='contained'
            onClick={() => void handleConfirmTxTypeDelete()}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {snackbar && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`rules-tabpanel-${String(index)}`}
      aria-labelledby={`rules-tab-${String(index)}`}
      {...other}
      style={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingTop: '16px',
      }}
    >
      {value === index && (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default ManageRulesModal;
